import React, { useContext, useState, useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { AgendaContext } from '../context/AgendaContext';
import InfoForm from './forms/InfoForm';
import ToggleForm from './ToggleForm';
import DocActionMenu from './DocActionMenu';
import { DocsWrapper, TableColumn } from '../styles/TableStyle';
import { useTranslation } from 'react-i18next';

const IecInfo = () => {
    const { t } = useTranslation();
    const { iecInfoDocs, setIecInfoDocs } = useContext(AgendaContext);

    const dynamicFields = [
        { type: 'text', label: t('common.formLabels.document.name'), name: 'document', placeholder: t('common.formLabels.document.label'), required: true },
        { type: 'textarea', label: t('common.formLabels.title.name'), name: 'title', placeholder: t('common.formLabels.title.label'), required: true },
        { type: 'date', label: t('infoDocs.formLabels.published.name'), name: 'published_date', placeholder: t('infoDocs.formLabels.published.label'), required: true },
    ]

    const [editItem, setEditItem] = useState({
        id: null,
        document: '',
        title: '',
        published_date: '',
        document_links: [{ fileName: '', url: '' }, { fileName: '', url: '' }],
    });

    const [newItem, setNewItem] = useState({
        id: null,
        document: '',
        title: '',
        published_date: '',
        document_links: [{ fileName: '', url: '' }, { fileName: '', url: '' }],
    });

    useEffect(() => {
        // Get items from local storage
        const storedItems = JSON.parse(localStorage.getItem('information_docs')) || []

        if (storedItems.length > 0) {
            // Map through the current docs and replace with the stored version available
            setIecInfoDocs((prevItems) => {
                const mergeItems = prevItems.map((doc) => {
                    const localStorageItems = storedItems.find((item) => item.id === doc.id)

                    // If an updated version exists and its different, return it to overwrite the original
                    if (localStorageItems && JSON.stringify(localStorageItems) !== JSON.stringify(doc)) {
                        return localStorageItems
                    }
                    return doc
                })

                // Get newly added items from local storage 
                const newItems = storedItems.filter((stored) => !prevItems.some((doc) => doc.id === stored.id))

                // Return the combined list: merged docs and new items from local storage
                return [...mergeItems, ...newItems]
            })
        }
    }, []);

    const handleEditCancel = () => {
        setEditItem(prev => ({
            ...prev,
            id: null
        }))
    };

    const handleEditSubmit = (event) => {
        event.preventDefault();
        const { document, title, published_date, document_links } = editItem;

        setIecInfoDocs((prevItem) => {
            const items = prevItem?.map((item) => {
                if (item.id === editItem.id) {
                    const updatedItem = {
                        ...item,
                        document: document,
                        title: title,
                        published_date: published_date,
                        document_links: document_links,
                    }

                    // Current list in local storage
                    const getItem = JSON.parse(localStorage.getItem('information_docs')) || [];

                    // Check if this task was already edited
                    const existingIndex = getItem.findIndex(i => i.id === updatedItem.id)

                    if (existingIndex !== -1) {
                        // Repace the item if it exsit
                        getItem[existingIndex] = updatedItem
                    } else {
                        // Otherwise save it to the list
                        getItem.push(updatedItem)
                    }

                    localStorage.setItem('information_docs', JSON.stringify(getItem));

                    return updatedItem
                }
                return item
            })

            return items
        });

        setEditItem({ id: null, document: '', title: '', published_date: '', document_links: [{ fileName: '', url: '' }, { fileName: '', url: '' }] });
    };

    const handleNewSubmit = (event) => {
        event.preventDefault();
        const { document, title, published_date, document_links } = newItem;

        const newDoc = {
            id: uuidv4(),
            document: document,
            title: title,
            published_date: published_date,
            document_links: document_links,
        }

        setIecInfoDocs((prevItem) => {
            const addNewDoc = [...prevItem, newDoc];
            const storedItems = JSON.parse(localStorage.getItem('information_docs')) || [];
            const updatedStoredDocs = [...storedItems, newDoc]
            localStorage.setItem('information_docs', JSON.stringify(updatedStoredDocs))

            return addNewDoc
        });


        setNewItem({ id: null, document: '', title: '', published_date: '', document_links: [{ fileName: '', url: '' }, { fileName: '', url: '' }] });
    };

    const handleDeleteText = (id) => {
        const deleteItem = iecInfoDocs.filter((item) => item.id !== id);
        setIecInfoDocs(deleteItem);
        localStorage.setItem('information_docs', JSON.stringify(deleteItem))
    };

    const handleEditTextClick = (item) => {
        setEditItem({
            id: item.id,
            document: item.document,
            title: item.title,
            published_date: item.published_date,
            document_links: item.document_links,
        });
    };

    const handleEditChange = (event) => {
        const { name, value } = event.target;
        if (name.startsWith('document_links')) {
            const index = parseInt(name.split('.')[1], 10); // get index 
            const updatedLinks = [...editItem.document_links];
            const property = name.split('.')[2] // get fileName or url
            updatedLinks[index] = { ...updatedLinks[index], [property]: value };
            setEditItem({ ...editItem, document_links: updatedLinks });
        } else {
            setEditItem({ ...editItem, [name]: value });
        }
    };

    const handleNewChange = (event) => {
        const { name, value } = event.target;
        if (name.startsWith('document_links')) {
            const index = parseInt(name.split('.')[1], 10); // get index 
            const updatedLinks = [...newItem.document_links];
            const property = name.split('.')[2] // get fileName or url 
            updatedLinks[index] = { ...updatedLinks[index], [property]: value };
            setNewItem({ ...newItem, document_links: updatedLinks });
        } else {
            setNewItem({ ...newItem, [name]: value });
        }
    };

    return (
        <div>
            <div>
                {iecInfoDocs && iecInfoDocs.length > 0 ? (
                    <DocsWrapper $type='information_docs'>
                        <div>
                            <span>{t('common.formLabels.document.name')}</span>
                            <span>{t('infoDocs.formLabels.published.name')}</span>
                            <span></span>
                        </div>

                        {iecInfoDocs.map((item, index) => (
                            <div key={item.id}>
                                {editItem.id === item.id ? (
                                    <InfoForm
                                        item={editItem}
                                        setItem={setEditItem}
                                        handleOnChange={handleEditChange}
                                        handleSubmit={handleEditSubmit}
                                        handleEditCancel={handleEditCancel}
                                        docs={iecInfoDocs.length}
                                        dynamicFields={dynamicFields}
                                        title={`${t('common.edit')} ${t('infoDocs.iecDocuments')} #${index + 1}`}
                                        buttonColorTheme='lightBlue'
                                        isEditing={editItem.id === item.id}
                                    />
                                ) : (
                                    <>
                                        <TableColumn>
                                            <p>{item.document}</p>
                                            <div>
                                                {item.document_links.map((link, index) => (
                                                    <a key={index} href={link.url} target='_blank' rel='noopener noreferrer'>{link.fileName}</a>
                                                ))}
                                            </div>
                                            <p>{item.title}</p>
                                        </TableColumn>
                                        <span>{new Date(item.published_date).toLocaleDateString()}</span>
                                        <span>
                                            <DocActionMenu
                                                item={item}
                                                handleDeleteText={handleDeleteText}
                                                handleEditTextClick={handleEditTextClick}
                                            />
                                        </span>
                                    </>
                                )}
                            </div>
                        ))}
                    </DocsWrapper>
                ) : (
                    <span>{t('common.noDocuments')}</span>
                )}
            </div>
            <ToggleForm>
                <InfoForm
                    item={newItem}
                    setItem={setNewItem}
                    handleOnChange={handleNewChange}
                    handleSubmit={handleNewSubmit}
                    dynamicFields={dynamicFields}
                    title={`${t('common.addBtnLabel')} ${t('infoDocs.iecDocuments')}`}
                    buttonColorTheme='darkBlue'
                />
            </ToggleForm>
        </div>
    );
};


export default IecInfo